import '@aws-amplify/ui-react/styles.css';
import './App.css';

import {
    AwsRum,
    AwsRumConfig
} from "aws-rum-web";
import {
    Box,
    CssBaseline,
    LinearProgress
} from "@mui/material";
import {
    QueryClient,
    QueryClientProvider
} from "react-query";
import {
    Suspense,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Switch,
    matchPath,
    useLocation
} from "react-router-dom";
import {
    clearInterval,
    setInterval
} from "timers";

import { CognitoUser as AmplifyUser } from "@aws-amplify/auth";
import { Auth } from 'aws-amplify';
import ClientLoggerFactory from './lib/logging/ClientLoggerFactory';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { CustomMeetingProvider } from './components/chime/customized/CustomMeetingProvider';
import { DataStoreInitializationLayer } from './components/sync/DataStoreInitializationLayer';
import DefaultAttachmentUploadManagerFactory from './lib/attachment/upload/manager/DefaultAttachmentUploadManagerFactory';
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from './components/general/ErrorFallback';
import { FABManager } from './components/general/fab/FABManager';
import { GeneratePNumberPage } from './pages/solution/generate/GeneratePNumberPage';
import GlobalStateComponent from './components/globalstate/GlobalStateComponent';
import LogRocket from 'logrocket';
import { PublicPropertyDashboardPage } from "./pages/property/PublicPropertyDashboardPage";
import { PublicSolutionDashboardPage } from './pages/solution/PublicSolutionDashboardPage';
import { QueryClientManager } from './lib/query/QueryClientManager';
import { RecoilRoot } from 'recoil';
import RoutesByAccountType from './pages/route/RoutesByAccountType';
import { SOFTWARE_VERSION } from './lib/util/troubleshooting/SoftwareVersion';
import Sidebar from "./components/sidebar/Sidebar";
import { SnackbarProvider } from 'notistack';
import { StyledEngineProvider } from '@mui/material/styles';
import SurveyJSGlobalStateSetupHandlerFactory from './lib/ui/SurveyJSGlobalStateSetupHandlerFactory';
import { UnauthorizedPagePaths } from './lib/auth/bypass/UnauthorizedPagePaths';
import { UnauthorizedRoute } from './lib/auth/bypass/UnauthorizedRoute';
import UpdateNotificationSnackbar from './components/globalstate/UpdateNotificationSnackbar';
import { UserProvider } from './components/user/UserContext';
import { VideoCall } from './pages/test/VideoCall';
import awsExport from './aws-exports';
import { sleep } from './lib/util/concurrency/Sleep';
import { useLoginEventBroadcast } from './lib/auth/LoginEventBroadcast';
import { withBrowserRouter } from "./components/providers/withBrowserRouter";
import { withSignInPortal } from "./components/auth/withSignInPortal";
import { withThemeProvider } from "./components/theme/withThemeProvider";

//username is defined outside of App so that username will get updated inside syncExpression, or else syncExpression will not get updated.
let username: string;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: true
        }
    }
});

QueryClientManager.initialize(queryClient);

export let awsRum: AwsRum | undefined;

try {
    // @ts-ignore
    const RUM_APPLICATION_ID = awsExport.aws_cloudwatch_rum_app_id;
    if (!RUM_APPLICATION_ID) {
        throw new Error("RUM_APPLICATION_ID is not defined");
    }

    const REGION = awsExport.aws_project_region ?? "us-east-2";
    const config: AwsRumConfig = {
        sessionSampleRate: 1,
        identityPoolId: awsExport.aws_cognito_identity_pool_id,
        endpoint: `https://dataplane.rum.${REGION}.amazonaws.com`,
        telemetries: ["performance", "errors", "http"],
        allowCookies: true,
        enableXRay: true,
        sessionEventLimit: 1000,
        recordResourceUrl: false,
    };

    awsRum = new AwsRum(
        RUM_APPLICATION_ID,
        SOFTWARE_VERSION,
        REGION,
        config
    );
} catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
}

const AuthenticatedPages = withSignInPortal(({ user }: { user: CognitoUser; }) => {
    const clientLoggerRef = useRef(ClientLoggerFactory.getClientLogger("Root"));
    const attachmentUploadManager = useRef(DefaultAttachmentUploadManagerFactory.getInstance());
    const surveyJSGlobalStateSetupHandler = useRef(SurveyJSGlobalStateSetupHandlerFactory.getInstance());

    username = useMemo(() => {
        return user?.getUsername();
    }, [user]);

    useEffect(() => {
        awsRum?.recordEvent('UserLoggedIn', { data: 'User logged in successfully' });
        let intervalId: any;
        if (user) {
            intervalId = setInterval(() => {
                attachmentUploadManager.current.initiateUploadCycle();
            }, 30000);
            surveyJSGlobalStateSetupHandler.current.setup();
            initializeLogRocket(user);
            refreshTokenUntilSuccess();
        }
        return () => {
            clearInterval(intervalId);
        };
    }, [username]);

    const initializeLogRocket = (user: AmplifyUser) => {
        // This identifies the user for this session in LogRocket
        LogRocket.identify(username);
    };

    const refreshTokenUntilSuccess = async () => {
        let shouldRefreshToken = true;
        while (shouldRefreshToken) {
            if (navigator.onLine) {
                try {
                    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
                    const currentSession = await Auth.currentSession();
                    await new Promise((resolve, reject) => {
                        cognitoUser.refreshSession(currentSession.getRefreshToken(), (err, session) => {
                            if (err || !session) {
                                reject(err);
                            }
                            resolve(session);
                        });
                    });
                    shouldRefreshToken = false;
                } catch (error) {
                    clientLoggerRef.current.warn(
                        "Refresh token failed, retry again after 30s",
                        error,
                        ["RefreshTokenFailure"]
                    );
                    shouldRefreshToken = true;
                }
            }
            await sleep(30000);
        }
    };
    return (
        <RecoilRoot key={username}>
            <StyledEngineProvider injectFirst={true}>
                <CustomMeetingProvider>
                    <UserProvider user={user}>
                        <CssBaseline />
                        <Sidebar userId={username}>
                            <ErrorBoundary fallbackRender={ErrorFallback}>
                                <DataStoreInitializationLayer user={user}>
                                    <GlobalStateComponent user={user} />
                                    <FABManager />
                                    <Box>
                                        <Suspense fallback={<LinearProgress />}>
                                            <RoutesByAccountType />
                                        </Suspense>
                                    </Box>
                                </DataStoreInitializationLayer>
                            </ErrorBoundary>
                        </Sidebar>
                    </UserProvider>
                </CustomMeetingProvider>
            </StyledEngineProvider>
        </RecoilRoot>
    );
});

const UnauthenticatedPages = () => {
    return (
        <RecoilRoot key="unauthenticated">
            <SnackbarProvider>
                <StyledEngineProvider injectFirst={true}>
                    <CustomMeetingProvider>
                        <CssBaseline />
                        <Switch>
                            <UnauthorizedRoute component={GeneratePNumberPage} path="/generate/pnumber" exact />
                            <UnauthorizedRoute component={VideoCall} path="/remote/inspection" exact />
                            <UnauthorizedRoute component={PublicPropertyDashboardPage} path="/public/dashboard/property/:propertyId" />
                            <UnauthorizedRoute component={PublicSolutionDashboardPage} path="/public/properties/:propertyId/solutions/:solutionId/dashboard" exact />
                        </Switch>
                    </CustomMeetingProvider>
                </StyledEngineProvider>
            </SnackbarProvider>
        </RecoilRoot>
    );
};

const App = () => {
    const [bypassAuth, setBypassAuth] = useState<boolean>(false);
    const location = useLocation();

    useEffect(() => {
        const isUnauthorizedPath = UnauthorizedPagePaths.some(path => {
            const match = matchPath(location.pathname, path);
            return match !== null;
        });
        if (isUnauthorizedPath) {
            setBypassAuth(true);
            return;
        }
        setBypassAuth(false);
    }, [location]);

    useEffect(() => {
        LogRocket.init('4w8z6m/property-manager');
    }, []);

    useLoginEventBroadcast();

    return (
        <ErrorBoundary fallbackRender={ErrorFallback}>
            <QueryClientProvider client={queryClient}>
                {!bypassAuth && <AuthenticatedPages />}
                {bypassAuth && <UnauthenticatedPages />}
                <UpdateNotificationSnackbar />
            </QueryClientProvider>
        </ErrorBoundary>
    );
};

export default withBrowserRouter(
    withThemeProvider(
        App
    )
);
